<template>
  <div class="admin-users-page">
    <header class="admin-users-bar">
      <button type="button" class="admin-users-back" @click="$emit('back')" :aria-label="$t('ui.goBack')">
        <i class="bi-chevron-left" aria-hidden="true"></i>
      </button>
      <h1 class="admin-users-title">{{ $t("ui.admin") }}</h1>
      <button
        type="button"
        class="admin-users-refresh"
        :disabled="loading"
        @click="load"
        :aria-label="$t('ui.adminRetry')"
      >
        <i class="bi-arrow-clockwise" aria-hidden="true"></i>
      </button>
    </header>

    <div class="admin-users-body">
      <p v-if="loading" class="admin-users-status">{{ $t("ui.adminLoading") }}</p>
      <p v-else-if="error" class="admin-users-error">
        {{ error }}
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="load">
          {{ $t("ui.adminRetry") }}
        </button>
      </p>
      <template v-else>
        <div class="admin-table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                <th>{{ $t("ui.adminEmail") }}</th>
                <th>{{ $t("ui.adminCreatedAt") }}</th>
                <th>{{ $t("ui.adminLastTodoAt") }}</th>
                <th>{{ $t("ui.adminTodoCount") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.id">
                <td>{{ row.email }}</td>
                <td>{{ formatTime(row.createdAt) }}</td>
                <td>{{ row.lastTodoAt ? formatTime(row.lastTodoAt) : "—" }}</td>
                <td>{{ row.todoCount }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="admin-pager">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="page <= 1 || loading"
            @click="goPage(page - 1)"
          >
            {{ $t("ui.adminPrevPage") }}
          </button>
          <span class="admin-pager-meta">{{ pageLabel }}</span>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="page >= totalPages || loading"
            @click="goPage(page + 1)"
          >
            {{ $t("ui.adminNextPage") }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
const { fetchAdminUsers } = require("../sync/engine");

const PAGE_SIZE = 50;

export default {
  name: "AdminUsersPage",
  emits: ["back"],
  data() {
    return {
      rows: [],
      loading: false,
      error: "",
      page: 1,
      pageSize: PAGE_SIZE,
      total: 0,
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize) || 1);
    },
    pageLabel() {
      return this.$t("ui.adminPageOf", {
        page: this.page,
        totalPages: this.totalPages,
        total: this.total,
      });
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    formatTime(iso) {
      if (!iso) return "";
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return iso;
      return date.toLocaleString();
    },
    goPage(next) {
      if (next < 1 || next > this.totalPages) return;
      this.page = next;
      this.load();
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        let json = await fetchAdminUsers({ page: this.page, pageSize: this.pageSize });
        this.total = typeof json.total === "number" ? json.total : (json.users || []).length;
        this.pageSize = typeof json.pageSize === "number" ? json.pageSize : this.pageSize;
        const lastPage = Math.max(1, Math.ceil(this.total / this.pageSize) || 1);
        if (this.page > lastPage) {
          this.page = lastPage;
          json = await fetchAdminUsers({ page: this.page, pageSize: this.pageSize });
          this.total = typeof json.total === "number" ? json.total : (json.users || []).length;
        }
        this.rows = json.users || [];
        this.page = typeof json.page === "number" ? json.page : this.page;
      } catch (err) {
        this.error = err.message || this.$t("ui.adminLoadFailed");
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-users-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--m-page, #f2f2f7);
}

.admin-users-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 10px 12px;
  background: var(--m-surface, #fff);
  border-bottom: 1px solid var(--m-hairline, rgba(60, 60, 67, 0.18));
}

.admin-users-back,
.admin-users-refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #0d6efd;
  cursor: pointer;
}

.admin-users-back:active,
.admin-users-refresh:active {
  background: rgba(120, 120, 128, 0.12);
}

.admin-users-refresh:disabled {
  opacity: 0.4;
  cursor: default;
}

.admin-users-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.admin-users-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  padding: 12px 16px 28px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.admin-users-status {
  margin: 0;
  font-size: 14px;
  color: #656d76;
}

.admin-table-wrap {
  overflow-x: auto;
  border-radius: 12px;
  background: var(--m-surface, #fff);
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.admin-table th,
.admin-table td {
  padding: 10px 12px;
  text-align: start;
  border-bottom: 1px solid rgba(60, 60, 67, 0.18);
  vertical-align: top;
}

.admin-table tr:last-child td {
  border-bottom: none;
}

.admin-table th {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #656d76;
  white-space: nowrap;
}

.admin-table td:first-child {
  word-break: break-word;
}

.admin-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.admin-pager-meta {
  flex: 1;
  text-align: center;
  font-size: 13px;
  color: #656d76;
}

.admin-users-error {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 14px;
  color: #b42318;
}
</style>
